import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { apiGet, apiPost } from "../lib/api";
import { Badge } from "./index";

interface Notification {
  id: string;
  type: string;
  subject: string | null;
  body: string | null;
  status: string;
  createdAt: string;
  readAt: string | null;
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  // Unread count (polls every 30s)
  const { data: countData } = useQuery<{ unreadCount: number }>({
    queryKey: ["notifications-unread"],
    queryFn: () => apiGet("/api/notifications/unread-count"),
    refetchInterval: 30_000,
  });

  // Recent notifications (only when dropdown is open)
  const { data: listData } = useQuery<{ notifications: Notification[] }>({
    queryKey: ["notifications-recent"],
    queryFn: () => apiGet("/api/notifications?limit=5"),
    enabled: open,
  });

  const markRead = useMutation({
    mutationFn: (id: string) => apiPost(`/api/notifications/${id}/read`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications-unread"] });
      queryClient.invalidateQueries({ queryKey: ["notifications-recent"] });
    },
  });

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const unread = countData?.unreadCount ?? 0;
  const notifications = listData?.notifications ?? [];
  const bellLabel = unread > 0 ? `Operator inbox, ${unread} unread` : "Operator inbox";

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        aria-label={bellLabel}
        onClick={() => setOpen(!open)}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          position: "relative",
          padding: 4,
          color: "var(--c-text-secondary)",
          display: "flex",
          alignItems: "center",
        }}
        title={bellLabel}
      >
        <svg
          aria-hidden="true"
          width={20}
          height={20}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0" />
        </svg>
        {unread > 0 && (
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              background: "var(--c-danger, #ef4444)",
              color: "#fff",
              fontSize: 10,
              fontWeight: 700,
              borderRadius: "50%",
              width: 16,
              height: 16,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            right: 0,
            width: 320,
            background: "var(--c-surface)",
            border: "1px solid var(--c-border, rgba(0,0,0,0.1))",
            borderRadius: 8,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            zIndex: 50,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "12px 16px",
              borderBottom: "1px solid var(--c-border, rgba(0,0,0,0.1))",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span style={{ fontSize: 14, fontWeight: 600, color: "var(--c-text)" }}>
              Operator inbox
            </span>
            {unread > 0 && <Badge color="blue">{unread} unread</Badge>}
          </div>

          <div style={{ maxHeight: 300, overflowY: "auto" }}>
            {notifications.length === 0 ? (
              <div
                style={{
                  padding: 24,
                  textAlign: "center",
                  color: "var(--c-text-faint)",
                  fontSize: 13,
                }}
              >
                No notifications yet
              </div>
            ) : (
              notifications.map((n) => (
                <div
                  key={n.id}
                  style={{
                    padding: "10px 16px",
                    borderBottom: "1px solid var(--c-border, rgba(0,0,0,0.05))",
                    background: n.readAt
                      ? "transparent"
                      : "color-mix(in srgb, var(--c-primary) 4%, transparent)",
                    cursor: n.readAt ? "default" : "pointer",
                  }}
                  onClick={() => {
                    if (!n.readAt) markRead.mutate(n.id);
                  }}
                >
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: n.readAt ? 400 : 500,
                      color: "var(--c-text)",
                    }}
                  >
                    {n.subject ?? n.type}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--c-text-faint)", marginTop: 2 }}>
                    {new Date(n.createdAt).toLocaleString()}
                  </div>
                </div>
              ))
            )}
          </div>

          <Link
            to="/actions"
            onClick={() => setOpen(false)}
            style={{
              display: "block",
              textAlign: "center",
              padding: "10px 16px",
              fontSize: 13,
              color: "var(--c-primary)",
              textDecoration: "none",
              borderTop: "1px solid var(--c-border, rgba(0,0,0,0.1))",
            }}
          >
            Open Action Center
          </Link>
        </div>
      )}
    </div>
  );
}
